import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ChevronLeft,
  Compass,
  Gauge,
  MapPin,
  Clock,
  Activity,
  Copy,
  ExternalLink,
  Hash,
  HeartPulse,
  Lock,
  Unlock,
  Pencil,
  Send,
  Check,
  Map as MapIcon,
  BarChart3,
  Share2,
  Shield,
  Wrench,
  Truck,
  KeyRound,
  X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { StatusBadge } from '@/components/device/StatusBadge';
import { BlockedBadge } from '@/components/device/BlockedBadge';
import { DevicePhoto } from '@/components/device/DevicePhoto';
import { reportsApi } from '@/api/endpoints/reports';
import { shareApi } from '@/api/endpoints/share';
import { devicesApi } from '@/api/endpoints/devices';
import { useFleetStore } from '@/stores/fleet';
import type { Device } from '@/api/types';
import { formatAddress } from '@/lib/address';
import {
  computeOperationalStatus,
  STATUS_LABELS,
  getDeviceNickname,
  isBlocked,
} from '@/lib/machineStatus';
import {
  cn,
  formatCoords,
  formatDateTime,
  formatRelativeTime,
  formatSpeed,
} from '@/lib/utils';
import {
  formatAttributeValue,
  groupConsecutiveByType,
  translateAttribute,
} from '@/lib/traccarLabels';
import { mensagemDeErro } from '@/lib/apiError';

export function DeviceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const deviceId = Number(id);

  const device = useFleetStore((s) => s.devices.get(deviceId));
  const position = useFleetStore((s) => s.positions.get(deviceId));

  const range = useMemo(() => {
    const to = new Date();
    const from = new Date(to.getTime() - 7 * 24 * 60 * 60 * 1000);
    return { from: from.toISOString(), to: to.toISOString() };
  }, []);

  const eventsQuery = useQuery({
    queryKey: ['events', deviceId, range.from, range.to],
    queryFn: () => reportsApi.events({ deviceId, from: range.from, to: range.to }),
    enabled: Number.isFinite(deviceId),
  });

  if (!device) {
    return (
      <div className="space-y-4 p-4 sm:p-6">
        <BackLink />
        <p className="text-muted-foreground">Embarcação não encontrada.</p>
      </div>
    );
  }

  const status = computeOperationalStatus(device, position);
  const nickname = getDeviceNickname(device);
  const blocked = isBlocked(device, position);

  return (
    <div className="space-y-5 p-4 sm:p-6">
      <BackLink title={device.name} />

      <div className="flex items-center gap-4">
        <DevicePhoto device={device} size="xl" />
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-xl font-bold tracking-tight">{device.name}</h1>
          {nickname && (
            <p className="truncate text-sm font-medium text-foreground/70">{nickname}</p>
          )}
          {device.model && (
            <p className="truncate text-sm text-muted-foreground">{device.model}</p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={status} size="md" />
        <BlockedBadge blocked={blocked} size="md" />
        <Link
          to={`/settings/devices/edit/${device.id}`}
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-accent"
        >
          <Pencil className="h-3.5 w-3.5" />
          Editar
        </Link>
        <ShareButton
          device={device}
          nickname={nickname}
          blocked={blocked}
          position={position}
        />
      </div>

      <section className="space-y-2">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Ações
        </h2>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <ActionPill icon={MapIcon} label="Abrir no mapa" to={`/map?device=${device.id}`} />
          <ActionPill icon={HeartPulse} label="Saúde" to={`/devices/${device.id}/health`} />
          <ActionPill icon={BarChart3} label="Relatórios" to={`/reports/route?device=${device.id}`} />
          <ShareLinkAction deviceId={device.id} />
        </div>
      </section>

      <section className="space-y-2">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Informações
        </h2>
        <Card>
          <CardContent className="p-0">
            <dl className="divide-y divide-border text-sm">
              <Field label="Nome / Registro" value={device.name} />
              <Field label="Modelo" value={device.model} />
              <Field label="Apelido" value={nickname || 'Não definido'} muted={!nickname} />
              <Field
                label="Última localização"
                value={formatDateTime(position?.fixTime ?? device.lastUpdate)}
              />
              <Field
                label="Endereço"
                value={formatAddress(position?.address) || formatCoords(position?.latitude, position?.longitude)}
              />
              <Field label="Status" value={STATUS_LABELS[status]} />
              <Field label="Bloqueio" value={blocked ? 'Bloqueada' : 'Desbloqueada'} />
              <Field label="Categoria" value={device.category} />
              <Field label="Telefone" value={device.phone} />
            </dl>
          </CardContent>
        </Card>
      </section>

      <SecuritySection device={device} />

      <DocumentosSection device={device} />

      <BlockedBanner blocked={blocked} />

      <Card>
        <CardHeader>
          <CardTitle>Localização náutica</CardTitle>
        </CardHeader>
        <CardContent>
          {position ? (
            <ul className="space-y-2 text-sm">
              <Row icon={Gauge} label="Velocidade" value={formatSpeed(position.speed)} />
              <Row
                icon={Compass}
                label="Rumo"
                value={position.course != null ? `${Math.round(position.course)}°` : '—'}
              />
              <Row
                icon={MapPin}
                label="Endereço"
                value={formatAddress(position.address) || formatCoords(position.latitude, position.longitude)}
              />
              <Row icon={Hash} label="Coordenadas" value={formatCoords(position.latitude, position.longitude)} />
              <Row
                icon={Clock}
                label="Hora da posição"
                value={`${formatDateTime(position.fixTime)} (${formatRelativeTime(position.fixTime)})`}
              />
              <Row
                icon={Activity}
                label="Recebido pelo servidor"
                value={formatDateTime(position.serverTime)}
              />
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Nenhuma posição conhecida.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Telemetria</CardTitle>
        </CardHeader>
        <CardContent>
          {position?.attributes && Object.keys(position.attributes).length > 0 ? (
            <dl className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-2 lg:grid-cols-3">
              {Object.entries(position.attributes).map(([k, v]) => (
                <div
                  key={k}
                  className="flex items-baseline justify-between gap-3 rounded-md border border-border bg-card px-3 py-2"
                >
                  <dt className="truncate font-medium text-muted-foreground" title={k}>
                    {translateAttribute(k)}
                  </dt>
                  <dd
                    className="truncate text-right font-semibold text-foreground"
                    title={String(v)}
                  >
                    {formatAttributeValue(k, v)}
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="text-sm text-muted-foreground">
              Nenhum dado de telemetria na última posição.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Eventos dos últimos 7 dias</CardTitle>
        </CardHeader>
        <CardContent>
          {eventsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Carregando eventos…</p>
          ) : eventsQuery.error ? (
            <p className="text-sm text-destructive">Erro ao carregar eventos.</p>
          ) : eventsQuery.data && eventsQuery.data.length > 0 ? (
            (() => {
              const grouped = groupConsecutiveByType(
                [...eventsQuery.data].sort(
                  (a, b) => new Date(b.serverTime).getTime() - new Date(a.serverTime).getTime(),
                ),
              );
              return (
                <ul className="divide-y divide-border text-sm">
                  {grouped.slice(0, 50).map((g, i) => (
                    <li
                      key={`${g.type}-${i}-${g.first.id}`}
                      className="flex items-center justify-between gap-3 py-2"
                    >
                      <span className="flex items-center gap-2">
                        <span className="font-medium">{g.label}</span>
                        {g.count > 1 && (
                          <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
                            ×{g.count}
                          </span>
                        )}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(g.last.serverTime)}
                      </span>
                    </li>
                  ))}
                </ul>
              );
            })()
          ) : (
            <p className="text-sm text-muted-foreground">Sem eventos no período.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ShareLinkAction({ deviceId }: { deviceId: number }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <ActionPill
        icon={Share2}
        label="Link público"
        onClick={() => setOpen(true)}
      />
      {open && <ShareLinkModal deviceId={deviceId} onClose={() => setOpen(false)} />}
    </>
  );
}

function ShareLinkModal({ deviceId, onClose }: { deviceId: number; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const mutation = useMutation({
    mutationFn: () => shareApi.createShareLink({ deviceId, icon: 'online' }),
  });

  // Gera o link automaticamente ao abrir
  useState(() => {
    mutation.mutate();
    return null;
  });

  const copy = async () => {
    if (!mutation.data?.url) return;
    try {
      await navigator.clipboard.writeText(mutation.data.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignora */
    }
  };

  const errorText = mensagemDeErro;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl bg-card p-5 shadow-xl ring-1 ring-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-2">
          <h3 className="text-base font-bold">Link público de localização</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {mutation.isPending ? (
          <p className="py-4 text-center text-sm text-muted-foreground">Gerando link…</p>
        ) : mutation.error ? (
          <div className="space-y-3">
            <p className="flex items-start gap-2 rounded-md bg-status-blocked/10 px-3 py-2 text-xs font-medium text-status-blocked">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {errorText(mutation.error)}
            </p>
            <button
              type="button"
              onClick={() => mutation.mutate()}
              className="text-xs font-semibold text-primary hover:underline"
            >
              Tentar novamente
            </button>
          </div>
        ) : mutation.data ? (
          <div className="space-y-3">
            <p className="rounded-md bg-status-ready/10 px-3 py-2 text-[11px] font-medium text-status-ready">
              Atenção: o token está vinculado ao seu usuário. Gerar novo link invalida links
              anteriores que você compartilhou.
            </p>

            <div className="flex items-stretch gap-2">
              <Input
                value={mutation.data.url}
                readOnly
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 font-mono text-[11px]"
              />
              <button
                type="button"
                onClick={copy}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-3 text-xs font-semibold hover:bg-accent"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? 'Copiado' : 'Copiar'}
              </button>
            </div>

            <a
              href={mutation.data.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Abrir em nova aba
            </a>

            <p className="text-[11px] text-muted-foreground">
              Validade: até {mutation.data.expiresAt.toLocaleDateString('pt-BR')}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function BlockedBanner({ blocked }: { blocked: boolean }) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium',
        blocked
          ? 'bg-status-blocked/10 text-status-blocked'
          : 'bg-status-running/10 text-status-running',
      )}
    >
      {blocked ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
      {blocked ? 'Motor bloqueado' : 'Motor desbloqueado'}
    </div>
  );
}

function ShareButton({
  device,
  nickname,
  blocked,
  position,
}: {
  device: { name: string; model?: string; uniqueId: string };
  nickname?: string;
  blocked: boolean;
  position?: { latitude?: number; longitude?: number; fixTime?: string; speed?: number };
}) {
  const [copied, setCopied] = useState(false);

  const buildText = () => {
    const lines: string[] = [];
    lines.push(`Embarcação: ${device.name}`);
    if (device.model?.trim()) lines.push(`Modelo: ${device.model}`);
    if (nickname) lines.push(`Apelido: ${nickname}`);
    lines.push(`Última localização: ${formatDateTime(position?.fixTime)}`);
    lines.push(`Status: ${blocked ? 'Bloqueada' : 'Desbloqueada'}`);
    if (position?.latitude != null && position?.longitude != null) {
      lines.push(`Coordenadas: ${formatCoords(position.latitude, position.longitude)}`);
      lines.push(`https://www.google.com/maps?q=${position.latitude},${position.longitude}`);
    }
    return lines.join('\n');
  };

  const handle = async () => {
    const text = buildText();
    if (typeof navigator !== 'undefined' && 'share' in navigator) {
      try {
        await navigator.share({ title: device.name, text });
        return;
      } catch {
        // usuário cancelou ou plataforma não suportou; cai pra clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // navegador antigo: ignora
    }
  };

  return (
    <button
      type="button"
      onClick={handle}
      className={cn(
        'inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-primary px-5 py-2.5',
        'text-sm font-semibold text-primary-foreground shadow-sm transition-colors',
        'hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
    >
      {copied ? <Check className="h-4 w-4" /> : <Send className="h-4 w-4" />}
      {copied ? 'Copiado' : 'Compartilhar'}
    </button>
  );
}

function ActionPill({
  icon: Icon,
  label,
  to,
  disabled,
  tooltip,
  onClick,
}: {
  icon: typeof MapIcon;
  label: string;
  to?: string;
  disabled?: boolean;
  tooltip?: string;
  onClick?: () => void;
}) {
  const className = cn(
    'flex flex-col items-center justify-center gap-1 rounded-xl px-3 py-3 transition-all',
    disabled
      ? 'cursor-not-allowed bg-primary/5 text-primary/40'
      : 'bg-primary/10 text-primary hover:bg-primary/15',
  );
  const inner = (
    <>
      <Icon className="h-5 w-5" />
      <span className="text-xs font-semibold">{label}</span>
    </>
  );
  if (to && !disabled) {
    return (
      <Link to={to} className={className} title={tooltip}>
        {inner}
      </Link>
    );
  }
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      title={tooltip}
      className={className}
    >
      {inner}
    </button>
  );
}

const SECURITY_MODES = [
  { key: 'normal', label: 'Normal', icon: Activity, hint: 'Operação normal.' },
  { key: 'seguranca', label: 'Segurança', icon: Shield, hint: 'Antifurto reforçado: alerta a qualquer movimento.' },
  { key: 'manutencao', label: 'Manutenção', icon: Wrench, hint: 'Em serviço: silencia alertas de movimento.' },
  { key: 'transporte', label: 'Transporte', icon: Truck, hint: 'Em carreta/reboque: ignora navegação.' },
  { key: 'locacao', label: 'Locação', icon: KeyRound, hint: 'Locada: monitora área e horário de retorno.' },
] as const;

function SecuritySection({ device }: { device: Device }) {
  const queryClient = useQueryClient();
  const current = (typeof device.attributes?.modo === 'string' ? (device.attributes.modo as string) : 'normal');
  const [pending, setPending] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (modo: string) =>
      devicesApi.update(device.id, {
        ...device,
        attributes: { ...(device.attributes ?? {}), modo },
      } as Device),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['devices'] });
      setPending(null);
    },
    onError: () => setPending(null),
  });

  const setMode = (modo: string) => {
    if (modo === current) return;
    setPending(modo);
    mutation.mutate(modo);
  };

  const active = SECURITY_MODES.find((m) => m.key === current) ?? SECURITY_MODES[0];

  return (
    <section className="space-y-2">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Modo de segurança
      </h2>
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            {SECURITY_MODES.map((m) => {
              const on = m.key === current;
              const loading = pending === m.key;
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setMode(m.key)}
                  disabled={mutation.isPending}
                  className={cn(
                    'flex flex-col items-center gap-1.5 rounded-xl px-2 py-3 text-xs font-semibold transition-colors',
                    on ? 'bg-brand text-brand-foreground' : 'bg-secondary text-muted-foreground hover:bg-accent',
                    mutation.isPending && !loading && 'opacity-50',
                  )}
                >
                  <m.icon className={cn('h-5 w-5', loading && 'animate-pulse')} />
                  {m.label}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">{active.hint}</p>
        </CardContent>
      </Card>
    </section>
  );
}

function DocumentosSection({ device }: { device: { attributes?: Record<string, unknown> } }) {
  const a = device.attributes ?? {};
  const s = (k: string) => (typeof a[k] === 'string' && (a[k] as string).trim() ? (a[k] as string) : null);
  const campos: { label: string; value: string | null }[] = [
    { label: 'Tipo', value: s('vesselType') },
    { label: 'Registro / Matrícula', value: s('registro') },
    { label: 'Nº do casco', value: s('casco') },
    { label: 'Motor', value: s('motor') },
    { label: 'Potência', value: s('potencia') },
    { label: 'Proprietário', value: s('proprietario') },
    { label: 'Seguradora', value: s('seguradora') },
    { label: 'Apólice', value: s('apolice') },
    { label: 'Marina', value: s('marina') },
  ].filter((c) => c.value);

  const vencimentos: { label: string; date: string | null }[] = [
    { label: 'Venc. do seguro', date: s('vencSeguro') },
    { label: 'Venc. do documento', date: s('vencDoc') },
  ].filter((v) => v.date);

  if (campos.length === 0 && vencimentos.length === 0) return null;

  const vencStatus = (iso: string) => {
    const target = new Date(`${iso}T23:59:59`).getTime();
    const days = Math.floor((target - Date.now()) / 86_400_000);
    if (days < 0) return { txt: 'Vencido', cls: 'bg-status-blocked/10 text-status-blocked' };
    if (days <= 30) return { txt: `Vence em ${days}d`, cls: 'bg-status-ready/10 text-status-ready' };
    return { txt: 'Em dia', cls: 'bg-status-running/10 text-status-running' };
  };

  return (
    <section className="space-y-2">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Documentos
      </h2>
      <Card>
        <CardContent className="p-0">
          <dl className="divide-y divide-border text-sm">
            {campos.map((c) => (
              <Field key={c.label} label={c.label} value={c.value} />
            ))}
            {vencimentos.map((v) => {
              const st = vencStatus(v.date!);
              return (
                <div key={v.label} className="grid grid-cols-[120px_1fr] items-center gap-3 px-4 py-2.5 sm:grid-cols-[160px_1fr]">
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">{v.label}</dt>
                  <dd className="flex items-center gap-2 text-sm font-semibold">
                    {new Date(`${v.date}T00:00:00`).toLocaleDateString('pt-BR')}
                    <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-bold', st.cls)}>{st.txt}</span>
                  </dd>
                </div>
              );
            })}
          </dl>
        </CardContent>
      </Card>
    </section>
  );
}

function BackLink({ title }: { title?: string }) {
  return (
    <div className="flex items-center justify-between">
      <Link
        to="/devices"
        className="-ml-2 inline-flex items-center gap-0.5 rounded-md px-2 py-1 text-sm font-medium text-primary hover:bg-primary/5"
      >
        <ChevronLeft className="h-5 w-5" />
        Voltar
      </Link>
      {title && (
        <span className="truncate text-sm font-semibold text-foreground/70">{title}</span>
      )}
      <span className="w-16" />
    </div>
  );
}

function Field({
  label,
  value,
  muted,
}: {
  label: string;
  value?: string | null;
  muted?: boolean;
}) {
  return (
    <div className="grid grid-cols-[120px_1fr] items-start gap-3 px-4 py-2.5 sm:grid-cols-[160px_1fr]">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={cn('text-sm font-semibold', (muted || !value) && 'font-normal text-muted-foreground')}>
        {value || '—'}
      </dd>
    </div>
  );
}

function Row({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Gauge;
  label: string;
  value: string;
}) {
  return (
    <li className="flex items-start justify-between gap-3">
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </span>
      <span className="text-right">{value}</span>
    </li>
  );
}
